/**
 * Settings → Notifications: AgencyAnalytics' fourteen events with a category
 * and an in-app and email switch each. A channel an event does not offer is
 * shown as not available; events whose feature is not built yet say so.
 * Each change saves at once, for the signed-in person only.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { inboxApi, type Preference } from './kpis-api.ts';

function Switch(props: {
  label: string;
  offered: boolean;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}): ReactNode {
  if (!props.offered)
    return (
      <span className="kp-faint" aria-label={`${props.label}: not offered`}>
        —
      </span>
    );
  return (
    <input
      type="checkbox"
      aria-label={props.label}
      checked={props.checked}
      disabled={props.disabled}
      onChange={(event) => props.onChange(event.target.checked)}
    />
  );
}

export function NotificationSettings({ organisationId }: { organisationId: string }): ReactNode {
  const [rows, setRows] = useState<Preference[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    inboxApi
      .preferences(organisationId)
      .then(setRows)
      .catch(() => setStatus('Your notification settings could not be loaded.'));
  }, [organisationId]);

  const change = (event: string, patch: Partial<Pick<Preference, 'in_app' | 'email'>>) => {
    const current = rows ?? [];
    const next = current.map((row) => (row.event === event ? { ...row, ...patch } : row));
    setRows(next);
    setStatus('Saving…');
    const changed = next.filter((row) => row.event === event);
    inboxApi
      .savePreferences(organisationId, changed)
      .then((saved) => {
        setRows(saved);
        setStatus('Saved.');
      })
      .catch(() => {
        setRows(current);
        setStatus('That change could not be saved.');
      });
  };

  return (
    <section className="card ag-settings" aria-labelledby="ns-title">
      <h2 id="ns-title">Notifications</h2>
      <p className="panel-note">
        Choose how you hear about each event: in the inbox (the bell), by email, or both. These
        settings are yours alone.
      </p>
      <p className="kp-faint ns-status" role="status">
        {status}
      </p>
      {rows && (
        <table className="ag-table ns-table">
          <thead>
            <tr>
              <th scope="col">Event</th>
              <th scope="col">Category</th>
              <th scope="col">In-app</th>
              <th scope="col">Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.event} className={row.is_available ? '' : 'ns-later'}>
                <th scope="row">
                  {row.label}
                  {!row.is_available && (
                    <small className="kp-faint kp-block">Arrives with a later feature</small>
                  )}
                </th>
                <td>{row.category}</td>
                <td>
                  <Switch
                    label={`${row.label} in-app`}
                    offered={row.in_app_offered}
                    checked={row.in_app}
                    disabled={!row.is_available}
                    onChange={(in_app) => change(row.event, { in_app })}
                  />
                </td>
                <td>
                  <Switch
                    label={`${row.label} by email`}
                    offered={row.email_offered}
                    checked={row.email}
                    disabled={!row.is_available}
                    onChange={(email) => change(row.event, { email })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
